import "./footer00.styles.css";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Phone, Mail } from "lucide-react";
import LinkedIn from "../../svg/socials/linkedin";
import FacebookIcon from "../../svg/socials/facebook";
import { partners } from "../../data/partners";
import { CONTACT_INFO, SOCIAL_MEDIA } from "../../config/constants";
import { sanitizeEmail } from "../../helpers/sanitization.helper";
import { getError } from "../../helpers/errors.helper";

const Footer00 = () => {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterError, setNewsletterError] = useState("");
  const [newsletterSuccess, setNewsletterSuccess] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newsletterEmail || !emailRegex.test(newsletterEmail)) {
      setNewsletterError("Please enter a valid email address");
      setNewsletterSuccess("");
      return;
    }

    try {
      setNewsletterLoading(true);
      setNewsletterError("");
      setNewsletterSuccess("");

      // Sanitize email input
      const sanitizedEmail = sanitizeEmail(newsletterEmail);

      // TODO: Replace with actual newsletter API endpoint when available
      // For now, this is a placeholder - backend integration needed
      // Example: await axios.post(`${API_CONFIG.baseURL}/newsletter`, { email: sanitizedEmail });
      
      // Simulate API call (remove this when backend is ready)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Uncomment when backend endpoint is available:
      // const { data } = await axios.post(
      //   `${API_CONFIG.baseURL}/newsletter`,
      //   { email: sanitizedEmail }
      // );
      
      setNewsletterSuccess("Thank you for subscribing! Check your email for confirmation.");
      setNewsletterEmail("");
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setNewsletterSuccess("");
      }, 5000);
      
      setNewsletterLoading(false);
    } catch (err) {
      setNewsletterLoading(false);
      setNewsletterError(getError(err) || "Failed to subscribe. Please try again later.");
      setNewsletterSuccess("");
    }
  };

  const handleLinkClick = (e, hash) => {
    if (window.location.pathname === '/contact') {
      e.preventDefault();
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <footer className="main-footer">
      {/* Partners Section */}
      <section className="footer-partners-section">
        <div className="footer-partners-container">
          {partners.map((partner) => (
            <a
              key={partner.id}
              id={partner.id}
              href={partner.link}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-partner-link"
              aria-label={`Visit ${partner.caption} partner website`}
            >
              <img 
                src={partner.logo} 
                alt={partner.alt || `${partner.caption} partner logo`}
                className="footer-partner-logo"
              />
            </a>
          ))}
        </div>
      </section>

      {/* Main Footer Content */}
      <div className="footer-main-content">
        <div className="footer-content-container">
          <div className="footer-columns">
            {/* Information Column */}
            <div className="footer-column">
              <h3 className="footer-column-title">Information</h3>
              <ul className="footer-links-list">
                <li>
                  <Link 
                    to="/contact#faqs"
                    onClick={(e) => handleLinkClick(e, 'faqs')}
                    className="footer-link"
                  >
                    FAQs
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact#contact-form"
                    onClick={(e) => handleLinkClick(e, 'contact-form')}
                    className="footer-link"
                  >
                    Request a Quote
                  </Link>
                </li>
                <li>
                  <Link to="/products" className="footer-link">Our Partners</Link>
                </li>
                <li>
                  <Link to="/products" className="footer-link">Products</Link>
                </li>
              </ul>
            </div>

            {/* Support Column */}
            <div className="footer-column">
              <h3 className="footer-column-title">Support</h3>
              <div className="footer-contact-info">
                <div className="footer-contact-item">
                  <Clock className="footer-contact-icon" />
                  <div className="footer-contact-details">
                    <span className="footer-contact-label">{CONTACT_INFO.businessHours.days}</span>
                    <span className="footer-contact-value">{CONTACT_INFO.businessHours.time}</span>
                  </div>
                </div>
                <div className="footer-contact-item">
                  <Phone className="footer-contact-icon" />
                  <div className="footer-contact-details">
                    <span className="footer-contact-label">Hotline</span>
                    <a href={`tel:${CONTACT_INFO.phone.mobile.replace(/\s/g, '')}`} className="footer-contact-value footer-contact-link">
                      {CONTACT_INFO.phone.mobile}
                    </a>
                  </div>
                </div>
                <div className="footer-contact-item">
                  <Mail className="footer-contact-icon" />
                  <div className="footer-contact-details">
                    <span className="footer-contact-label">Support</span>
                    <Link to="/contact" className="footer-contact-value footer-contact-link">
                      {CONTACT_INFO.email.primary}
                    </Link>
                  </div>
                </div>
                <div className="footer-contact-item">
                  <Mail className="footer-contact-icon" />
                  <div className="footer-contact-details">
                    <span className="footer-contact-label">Sales</span>
                    <a href={`mailto:${CONTACT_INFO.email.secondary}`} className="footer-contact-value footer-contact-link">
                      {CONTACT_INFO.email.secondary}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media & Newsletter Column */}
            <div className="footer-column footer-column-newsletter">
              <h3 className="footer-column-title">Stay Connected</h3>
              <div className="footer-social-media">
                <a
                  href={SOCIAL_MEDIA.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  aria-label="Visit Healix Medical on LinkedIn"
                >
                  <LinkedIn />
                </a>
                <a
                  href={SOCIAL_MEDIA.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  aria-label="Visit Healix Medical on Facebook"
                >
                  <FacebookIcon />
                </a>
              </div>
              
              <div className="footer-newsletter">
                <h4 className="footer-newsletter-title">Newsletter</h4>
                <p className="footer-newsletter-description">
                  Sign up for our newsletters to get our latest news
                </p>
                <form onSubmit={handleNewsletterSubmit} className="footer-newsletter-form">
                  <div className="footer-newsletter-input-group">
                    <label htmlFor="newsletter-email" className="sr-only">
                      Email address for newsletter
                    </label>
                    <input 
                      id="newsletter-email"
                      type="email" 
                      placeholder="Enter your email"
                      aria-label="Email address for newsletter subscription"
                      value={newsletterEmail}
                      onChange={(e) => {
                        setNewsletterEmail(e.target.value);
                        setNewsletterError("");
                      }}
                      required
                      disabled={newsletterLoading}
                      className="footer-newsletter-input"
                    />
                    <button 
                      type="submit"
                      className="footer-newsletter-btn" 
                      aria-label="Submit newsletter subscription"
                      disabled={newsletterLoading}
                    >
                      {newsletterLoading ? "Subscribing..." : "Subscribe"}
                    </button>
                  </div>
                  {newsletterError && (
                    <div className="footer-newsletter-error" role="alert" aria-live="polite">
                      {newsletterError}
                    </div>
                  )}
                  {newsletterSuccess && (
                    <div className="footer-newsletter-success" role="alert" aria-live="polite">
                      {newsletterSuccess}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="footer-bottom">
        <div className="footer-bottom-container">
          <p className="footer-copyright">
            &copy; {new Date().getFullYear()} Healix Medical. All Rights Reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer00;
